import { TeamInvitation } from './components/TeamInvitation';
import { useCallback, useEffect, useState } from 'react';
import { api, hasSession, prepareSession, sessionStarted, signOut, type Me } from './lib/api';
import { SyncProvider } from './lib/sync';
import { PlanProvider } from './lib/plan';
import { UpgradeDialog } from './components/UpgradeDialog';
import { dropPush, refreshPush } from './lib/push';
import { AuthScreen } from './components/AuthScreen';
import { Landing } from './components/Landing';
import { Shell } from './components/Shell';
import { GuestApp } from './components/GuestApp';
import { LEGAL_TITLES, LegalView, type LegalPage } from './components/Legal';
import { ToastProvider } from './components/ui';

// Token aus dem URL-Fragment übernehmen (Anmelde-Link); das Fragment geht nie
// an den Server. Der Server macht daraus ein Cookie, im Browser bleibt nichts.
const linkToken = location.hash.startsWith('#token=') ? decodeURIComponent(location.hash.slice(7)) : null;
if (linkToken) history.replaceState(null, '', location.pathname + location.search);
// Offenes Abmelden nachholen, Token aus Link oder altem Ablageort übernehmen
const ready = prepareSession(linkToken);

type PublicPage = 'start' | 'login' | 'registrieren' | LegalPage;

const LEGAL = Object.keys(LEGAL_TITLES) as LegalPage[];
const isLegal = (p: PublicPage): p is LegalPage => (LEGAL as string[]).includes(p);

function pageFromPath(): PublicPage {
  const p = location.pathname.replace(/\/$/, '').slice(1);
  if (p === 'login' || p === 'registrieren') return p;
  return (LEGAL as string[]).includes(p) ? (p as LegalPage) : 'start';
}

// Freigabe-Links für Externe: /g/<token>
const guestLink = location.pathname.match(/^\/g\/([A-Za-z0-9_-]{20,64})\/?$/)?.[1] ?? null;
// Freigabe aus einem fremden Team, der das eigene Konto beigetreten ist
const joinedShare = location.pathname.match(/^\/geteilt\/([0-9a-f-]{36})\/?$/)?.[1] ?? null;

export function App() {
  const invite = location.pathname.match(/^\/einladung\/([A-Za-z0-9_-]{43})\/?$/)?.[1];
  if (invite) return <ToastProvider><TeamInvitation token={invite} /></ToastProvider>;
  if (joinedShare) {
    return (
      <ToastProvider>
        <GuestApp link={joinedShare} joined />
      </ToastProvider>
    );
  }
  if (guestLink) {
    return (
      <ToastProvider>
        <GuestApp link={guestLink} />
      </ToastProvider>
    );
  }
  return <TeamApp />;
}

function TeamApp() {
  const [page, setPage] = useState<PublicPage>(pageFromPath);
  const go = (p: PublicPage) => {
    setPage(p);
    history.pushState(null, '', p === 'start' ? '/' : `/${p}`);
    window.scrollTo(0, 0);
  };
  useEffect(() => {
    document.title = isLegal(page) ? `${LEGAL_TITLES[page]} – Mach's` : "Mach's – Aufgaben wie im Chat. Mach's einfach...";
  }, [page]);
  useEffect(() => {
    const onPop = () => setPage(pageFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<'laden' | 'abgemeldet' | 'bereit'>(linkToken || hasSession() ? 'laden' : 'abgemeldet');

  const loadMe = useCallback(async () => {
    try {
      // Erst die Übernahme aus Anmelde-Link bzw. altem Ablageort abwarten
      await ready;
      const data = await api<Me>('/auth/me');
      setMe(data);
      setState('bereit');
      void refreshPush();
      try {
        localStorage.setItem('machs.me', JSON.stringify(data));
      } catch {
        // egal
      }
    } catch (err) {
      // Offline: mit dem zuletzt bekannten Benutzer weiterarbeiten
      const cached = (() => {
        try {
          return JSON.parse(localStorage.getItem('machs.me') ?? 'null') as Me | null;
        } catch {
          return null;
        }
      })();
      if (cached && (err as { status?: number }).status === 0) {
        setMe(cached);
        setState('bereit');
      } else {
        void signOut();
        setState('abgemeldet');
      }
    }
  }, []);

  useEffect(() => {
    if (state === 'laden') void loadMe();
  }, [state, loadMe]);

  useEffect(() => {
    const out = () => {
      void dropPush().finally(() => void signOut());
      setMe(null);
      setState('abgemeldet');
    };
    const reload = () => void loadMe();
    window.addEventListener('machs:logout', out);
    window.addEventListener('machs:reload-me', reload);
    return () => {
      window.removeEventListener('machs:logout', out);
      window.removeEventListener('machs:reload-me', reload);
    };
  }, [loadMe]);

  // Rechtstexte und Hilfe sind unabhängig von der Anmeldung erreichbar
  if (isLegal(page)) {
    return (
      <ToastProvider>
        <LegalView key={page} page={page} onHome={() => go('start')} onNavigate={go} />
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      {state === 'laden' && <div className="boot"><div className="spinner" /></div>}
      {state === 'abgemeldet' && page === 'start' && <Landing onLogin={() => go('login')} onRegister={() => go('registrieren')} onNavigate={go} />}
      {state === 'abgemeldet' && page !== 'start' && (
        <AuthScreen
          key={page}
          initialMode={page === 'registrieren' ? 'register' : 'login'}
          onBack={() => go('start')}
          onToken={() => {
            // Der Server hat die Anmeldung schon als Cookie gesetzt
            sessionStarted();
            const invite = new URLSearchParams(location.search).get('einladung');
            if (invite && /^[A-Za-z0-9_-]{43}$/.test(invite)) { location.href = `/einladung/${invite}`; return; }
            history.replaceState(null, '', '/');
            setState('laden');
          }}
        />
      )}
      {state === 'bereit' && me && (
        <SyncProvider key={`${me.tenant.id}:${me.user.id}:${me.user.name}`} me={me}>
          <PlanProvider>
            <Shell onSwitchTeam={() => (sessionStarted(), setState('laden'))} />
            <UpgradeDialog />
          </PlanProvider>
        </SyncProvider>
      )}
    </ToastProvider>
  );
}
